import { isNumber, isPlainObject } from "lodash-es";
import { from, Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";

import { CachePutOptions, StoreContainer } from "./cache.model";
import { createStoreContainer } from "./cache.util";

export interface CacheStore {
	/**
	 * Get item for key.
	 */
	getItem<T>(key: string): Promise<T | null>;

	/**
	 * Get item for key.
	 */
	getItemAsObservable<T>(key: string): Observable<T | null>;

	/**
	 * Get object for key.
	 */
	getObjectAsObservable<T>(key: string): Observable<StoreContainer<T> | null>;

	/**
	 * Set item for key with options.
	 */
	setItem<T>(key: string, value: T, options?: CachePutOptions): Promise<T>;

	/**
	 * Set item for key with options.
	 */
	setItemAsObservable<T>(key: string, value: T, options?: CachePutOptions): Observable<T>;

	/**
	 * Attempts to get item for key.
	 * If item is not in store, execute fetch callback, store result in cache and return result.
	 */
	getOrSet<T>(key: string, fetch: () => Promise<T>, options?: CachePutOptions): Promise<T>;

	/**
	 * Attempts to get item for key.
	 * If item is not in store, execute fetch callback, store result in cache and return result.
	 */
	getOrSetAsObservable<T>(key: string, fetch: () => Observable<T>, options?: CachePutOptions): Observable<T>;

	/**
	 * Removes item for key, if present.
	 */
	removeItem(key: string): Promise<void>;

	/**
	 * Removes item for key, if present.
	 */
	removeItemAsObservable(key: string): Observable<void>;

	/**
	 * Clears cache.
	 */
	clear(): Promise<void>;

	/**
	 * Clears cache.
	 */
	clearAsObservable(): Observable<void>;
}

/**
 * todo - rename class to something more appropriate
 *
 * todo - separate logic in this class to two separate layers
 * ( top layer has expiration and other cache specific logic )
 * ( bottom layer thinly wraps third-party storage library to create abstraction )
 */
export class GlobalCacheStore implements CacheStore {
	constructor(
		private cache: LocalForage,
		private cachePutOptions: CachePutOptions
	) {}

	getItemObject<T>(key: any): Promise<StoreContainer<T> | null> {
		return this.cache.getItem(key).then((cacheResult: StoreContainer<T>) => {
			const currentTimeInMilliseconds = new Date().getTime();

			if (isPlainObject(cacheResult)) {
				if (currentTimeInMilliseconds >= cacheResult.expires) {
					return this.cache.removeItem(key).then(() => null);
				}
				return cacheResult;
			}

			return null;
		});
	}

	getItem<T>(key: string): Promise<T | null> {
		return this.getItemObject<T>(key).then((object) => object?.value || null);
	}

	getObjectAsObservable<T>(key: string): Observable<StoreContainer<T> | null> {
		return from(this.getItemObject<T>(key));
	}

	getItemAsObservable<T>(key: string): Observable<T | null> {
		return from(this.getItem<T>(key));
	}

	setItem<T>(key: string, value: T, cachePutOptions?: CachePutOptions): Promise<T> {
		const options = Object.assign({}, this.cachePutOptions, cachePutOptions);
		const currentTimeInMilliseconds = new Date().getTime();
		let expiryTimeInMilliseconds = Number.MAX_VALUE;
		if (isNumber(options.maxAge) && options.maxAge !== Number.MAX_VALUE) {
			const potentialExpiryTimeInMillis = new Date(currentTimeInMilliseconds + options.maxAge).getTime();
			if (!isNaN(potentialExpiryTimeInMillis)) {
				expiryTimeInMilliseconds = potentialExpiryTimeInMillis;
			}
		}
		return this.cache
			.setItem(key, createStoreContainer<T>(key, value, currentTimeInMilliseconds, expiryTimeInMilliseconds))
			.then((cacheResult) => cacheResult.value); // todo: check if this is optional
	}

	setItemAsObservable<T>(key: string, value: T, options?: CachePutOptions): Observable<T> {
		return from(this.setItem<T>(key, value, options));
	}

	getOrSet<T>(key: string, fetch: () => Promise<T>, options?: CachePutOptions): Promise<T> {
		return this.getItem<T>(key).then((cacheResult) => {
			if (cacheResult !== null) {
				return cacheResult;
			}

			return fetch().then((x) => this.setItem<T>(key, x, options));
		});
	}

	getOrSetAsObservable<T>(key: string, fetch: () => Observable<T>, options?: CachePutOptions): Observable<T> {
		return this.getItemAsObservable<T>(key).pipe(
			mergeMap((cacheResult) => {
				if (cacheResult !== null) {
					return of(cacheResult);
				}

				return fetch().pipe(mergeMap((x) => this.setItemAsObservable<T>(key, x, options)));
			})
		);
	}

	removeItem(key: string): Promise<void> {
		return this.cache.removeItem(key);
	}

	removeItemAsObservable(key: string): Observable<void> {
		return from(this.removeItem(key));
	}

	clear(): Promise<void> {
		return this.cache.clear();
	}

	clearAsObservable(): Observable<void> {
		return from(this.clear());
	}
}
